import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { CommonPrintService, workbookPrintService } from 'src/service/print'

import _Button from '@/component/atoms/Button/_Button/_Button'
import { default as Checkbox } from '@/component/atoms/Checkbox/_Checkbox'
import { default as CheckboxButton } from '@/component/atoms/CheckboxButton'
import { Dropdown, DropdownItem } from '@/component/molecules/Dropdown'
import { default as Input } from '@/component/molecules/Input'
import { default as PlusMinusInput } from '@/component/molecules/PlusMinusInput'
import { Radio } from '@/component/molecules/Radio'
import CustomPrintModalBanner from '@/page/CustomPrint/CustomPrintModalBanner'
import modalService from '@/service/modal.service'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import S from './Print.style'

type Props = {
  printTargetIds: number[]
  afterPrintFn: () => void
}

export const WorkbookPrintModal = observer(({ printTargetIds, afterPrintFn }: Props) => {
  useEffect(() => {
    workbookPrintService.fetchInitPrintList(printTargetIds)
  }, [])

  const onPrint = () => {
    workbookPrintService.onPrint(printTargetIds)
    afterPrintFn()
  }

  if (workbookPrintService.printPageInfoList === null) {
    return null
  }

  return (
    <section css={[S.Wrap, S.Worksheet]}>
      <header>
        <h3>교재 인쇄</h3>
      </header>
      <CustomPrintModalBanner
        onClick={() => {
          modalService.closeModal()
          mixpanelClient.track('T62-03_커스텀프린팅_진입점_교재인쇄배너')
        }}
      />
      <div className="print-body">
        <div className="print-row print-count">
          <p className="row-left">매수</p>
          <div className="row-right">
            <PlusMinusInput
              max={CommonPrintService.MAX_PRINT_QUANTITY}
              value={workbookPrintService.printOption.copyNumber}
              onChange={(copyNumber) => {
                workbookPrintService.printOption.update({ copyNumber })
              }}
              editable
            />
          </div>
        </div>
        <div className="print-row print-range">
          <p className="row-left">범위</p>
          <div className="row-right">
            <Radio.Group
              className="range-radio"
              value={workbookPrintService.uiStore.visibleRangeOption}
              onChange={(event) => {
                workbookPrintService.uiStore.update({
                  visibleRangeOption: event.target.value as 'ALL' | 'SELECT',
                })

                workbookPrintService.onChangeRangeOption(workbookPrintService.rangeOption)
              }}
            >
              <Radio value="ALL">
                전체 &nbsp;
                {workbookPrintService.isSinglePrintTarget && (
                  <span className="all-page-range">({workbookPrintService.maximumPageRange})</span>
                )}
              </Radio>
              <Radio value="SELECT">페이지 선택</Radio>
            </Radio.Group>
            {workbookPrintService.uiStore.visibleRangeOption === 'SELECT' && (
              <>
                {workbookPrintService.isSinglePrintTarget && (
                  <div>
                    <div className="range-insert-area">
                      <Input
                        type="text"
                        placeholder="페이지를 입력해주세요."
                        className={`${
                          !workbookPrintService.rangeOption.isRangedInputText ? 'warning' : ''
                        }`}
                        value={workbookPrintService.rangeOption.inputText}
                        onChange={(event) =>
                          workbookPrintService.onChangePageInput(event.target.value)
                        }
                      />
                      <p>예시) 1,3,5-7</p>
                    </div>
                    {!workbookPrintService.rangeOption.isRangedInputText && (
                      <p className="warning page-error">페이지를 범위에 맞게 입력해주세요.</p>
                    )}
                  </div>
                )}
                <div className="range-option-area">
                  <CheckboxButton
                    checked={workbookPrintService.rangeOption.problem}
                    onClick={() =>
                      workbookPrintService.onChangeRangeOption({
                        problem: !workbookPrintService.rangeOption.problem,
                      })
                    }
                  >
                    문제지
                  </CheckboxButton>
                  <CheckboxButton
                    checked={workbookPrintService.rangeOption.answer}
                    onClick={() =>
                      workbookPrintService.onChangeRangeOption({
                        answer: !workbookPrintService.rangeOption.answer,
                      })
                    }
                  >
                    정답해설
                  </CheckboxButton>
                </div>
              </>
            )}
          </div>
        </div>
        {workbookPrintService.printAgent && (
          <div className="print-row printer-select">
            <p className="row-left">프린터 선택</p>
            <div className="row-right">
              <div className="print-dropdown">
                <Dropdown
                  height={40}
                  width={344}
                  listHeight={120}
                  value={`${workbookPrintService.printAgent.selectedPrinter?.machineId}:${workbookPrintService.printAgent?.selectedPrinter?.displayName}`}
                  onChange={(value: string) => {
                    const [mid, printName] = value.split(':')
                    const printer = workbookPrintService.printAgent?.printerList.find(
                      ({ displayName, machineId }) =>
                        displayName === printName && machineId === mid,
                    )

                    workbookPrintService.selectPrinter(printer!)
                  }}
                >
                  {workbookPrintService.printAgent.printerList.map((printer) => (
                    <DropdownItem
                      key={`${printer.machineId}:${printer.displayName}`}
                      data-mathflat-unique-id={`${printer.machineId}:${printer.displayName}`}
                      value={`${printer.machineId}:${printer.displayName}`}
                    >
                      {printer.displayName}
                    </DropdownItem>
                  ))}
                </Dropdown>
              </div>
            </div>
          </div>
        )}
        <div className="print-row print-option">
          <p className="row-left">옵션</p>
          <div className="row-right ">
            <Checkbox
              onChange={(isDuplex) => workbookPrintService.printOption.update({ isDuplex })}
              checked={workbookPrintService.printOption.isDuplex}
              text={<span>양면</span>}
            />
            <Checkbox
              onChange={(isMonochrome) => workbookPrintService.printOption.update({ isMonochrome })}
              checked={workbookPrintService.printOption.isMonochrome}
              text={<span>흑백</span>}
            />
            <Checkbox
              onChange={(isReverse) => workbookPrintService.printOption.update({ isReverse })}
              checked={workbookPrintService.printOption.isReverse}
              text={<span>역순</span>}
            />
            <Checkbox
              onChange={(excludeCoverPage) =>
                workbookPrintService.printOption.update({ excludeCoverPage })
              }
              checked={workbookPrintService.printOption.excludeCoverPage}
              text={<span>내지 제거</span>}
            />
          </div>
        </div>
      </div>
      <div className="print-footer">
        <_Button
          minWidth={144}
          size="m"
          disabled={workbookPrintService.disabledPrint}
          theme="primary"
          onClick={onPrint}
        >
          인쇄하기
        </_Button>
      </div>
    </section>
  )
})
